import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
    // Sync inline styles with the server-rendered inert state so JS always
    // controls grid-template-rows (avoids relying on the [inert] CSS selector,
    // which doesn't reliably trigger grid transitions in Chrome/Safari mobile).
    this.#applyStyles(this.refs.disclosureContent?.inert ?? false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.inert = expanded;
    this.#applyStyles(expanded);
  };

  /**
   * Directly sets grid-template-rows and opacity on the content element.
   * This is more reliable than the [inert] CSS attribute selector, which can
   * fail to trigger grid transitions in Chrome and Safari on mobile.
   * @param {boolean} collapsed - true when the panel should be hidden
   */
  #applyStyles(collapsed) {
    const { disclosureContent: content } = this.refs;
    if (!content) return;
    content.style.gridTemplateRows = collapsed ? '0fr' : '1fr';
    content.style.opacity = collapsed ? '0' : '1';
  }
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}